import '../App.css';
import {
  AppBar,
  IconButton,
  Tooltip,
  Box,
  useTheme as useMuiTheme,
  Select,
  MenuItem,
  FormControl,
  Button,
  Divider,
  alpha,
  Typography,
  styled,
  SelectChangeEvent,
  LinearProgress,
  CircularProgress,
  Chip,
} from '@mui/material';
import { angleOptionMultipliers, useUnityBuildStore } from '../stores/store';
import { ResetStates } from '../stores/resetStates';
import { useSubmitTakeoffAssembly } from '../hooks/useSubmitTakeoffAssembly';
import { useFetchCountedAssemblies } from '../hooks/useFetchCountedAssemblies';
import { useEffect, useRef, useState } from 'react';
import { CustomScale } from './CustomScale';
import { useStore } from 'zustand';
import { useAssemblyStore } from '../stores/AssemblyStore';
import { SCALE_OPTIONS } from '../utils/constants';
import { ToolBarHelper } from '../hooks/toolbarHooks';
import { useFetchObjects } from '../hooks/objectHooks';
import { DocumentManagerToggle } from './documents/DocumentManagerToggle';
import { ServiceHealthCheck } from './ServiceHealthCheck';
import { useTheme } from '../contexts/ThemeContext';

// Icons
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AddLocationIcon from '@mui/icons-material/AddLocation';
import StraightenIcon from '@mui/icons-material/Straighten';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ScaleIcon from '@mui/icons-material/Scale';
import InfoIcon from '@mui/icons-material/Info';
import EditNoteIcon from '@mui/icons-material/EditNote';
import GestureIcon from '@mui/icons-material/Gesture';
import FlipIcon from '@mui/icons-material/Flip';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import HeightIcon from '@mui/icons-material/Height';
import RectangleIcon from '@mui/icons-material/Rectangle';
import CircleIcon from '@mui/icons-material/Circle';
import TimelineIcon from '@mui/icons-material/Timeline';
import PolylineIcon from '@mui/icons-material/Polyline';
import CategoryIcon from '@mui/icons-material/Category';
import FormatColorFillIcon from '@mui/icons-material/FormatColorFill';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LinkIcon from '@mui/icons-material/Link';
// import SelectAllIcon from '@mui/icons-material/SelectAll';

import { Object as ProtoObject } from '../api/protosCompiled/object/object_pb';
import { useKeyBoardEventStore } from '../stores/keyEventStore';
import { getContrastTextColor } from '../utils/getCintrastTextColor';
import { useAnnotationStore } from './annotations/annotationStore';
import { useKeyboardEvents } from '../eventManager/keyboardEvents/useKeyboardEvents';
import { useSelectionStore } from '../stores/SelectionStore';
import { useStoreWithEqualityFn } from 'zustand/traditional';
import { shallow } from 'zustand/shallow';
import { useLockBid } from '../utils/useLockBid';
import { Z_INDEX_LAYERS } from '../utils/zIndexLayers';
import { TakeoffAssembly } from '../types/AssemblyItemType';

type ExtendedObject = ProtoObject.AsObject & {
  previewUrl: string;
};

// Custom styled select for better theme integration
interface StyledSelectProps {
  darkMode: boolean;
}

const StyledSelect = styled(Select, {
  shouldForwardProp: prop => prop !== 'darkMode',
})<StyledSelectProps>(({ theme, darkMode }) => ({
  height: 36,
  minWidth: 120,
  backgroundColor: darkMode
    ? alpha(theme.palette.background.paper, 0.15)
    : theme.palette.background.paper,
  color: darkMode ? theme.palette.common.white : theme.palette.common.black,
  borderRadius: 4,
  '&:hover': {
    backgroundColor: darkMode
      ? alpha(theme.palette.background.paper, 0.25)
      : alpha(theme.palette.background.paper, 0.9),
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: darkMode
      ? alpha(theme.palette.common.white, 0.23)
      : alpha(theme.palette.common.black, 0.23),
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },
  '& .MuiSelect-select': {
    paddingRight: 32,
    color: darkMode ? theme.palette.common.white : theme.palette.common.black,
  },
  '& .MuiSelect-icon': {
    color: darkMode
      ? alpha(theme.palette.common.white, 0.7)
      : alpha(theme.palette.common.black, 0.54),
  },
  '& .MuiPopover-root, & .MuiMenu-paper': {
    zIndex: Z_INDEX_LAYERS.DROPDOWNS.SELECT_MENUS,
  },
}));

/**
 * Return true if there is a current takeoff assembly
 * @param currentTakeoffAssembly - The current takeoff assembly
 * @returns True if there is a current takeoff assembly, false otherwise
 */
const haveCurrentTakeoffAssembly = (currentTakeoffAssembly: TakeoffAssembly) => {
  if (currentTakeoffAssembly) {
    return true;
  }
  return false;
};

// Custom SVG Icon Component
const AngleSnapIcon = ({ isActive, color }: { isActive: boolean; color: string }) => {
  const { darkMode } = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        mr: 1,
      }}
    >
      <img
        src="/icons/angle-snap.svg"
        alt="Angle Snap"
        style={{
          width: '32px',
          height: '32px',
          filter: isActive
            ? `brightness(0) saturate(100%) ${darkMode ? 'invert(60%) sepia(75%) saturate(1000%) hue-rotate(175deg) brightness(90%)' : 'invert(35%) sepia(100%) saturate(1000%) hue-rotate(195deg) brightness(100%)'}`
            : darkMode
              ? 'brightness(0) saturate(100%) invert(100%)'
              : 'none',
        }}
      />
    </Box>
  );
};

export const ToolBar = () => {
  const muiTheme = useMuiTheme();
  const { darkMode } = useTheme();
  const ubs = useStore(useUnityBuildStore);
  const globalAssemblyStore = useStore(useAssemblyStore);
  const { lockBid } = useLockBid();

  const { data: countedAssemblies } = useFetchCountedAssemblies();

  const { currentTakeoffAssembly } = useStoreWithEqualityFn(
    useAssemblyStore,
    state => ({
      currentTakeoffAssembly: state.currentTakeoffAssembly,
    }),
    shallow
  );

  const {
    setAngleOptions,
    isAnnotatingTakeoff,
    setIsAnnotatingTakeoff,
    setUserScale,
    selectedObject,
    setSelectedObject,
    isLassoMode,
    setIsLassoMode,
    setIsLassoDrawing,
    setLassoPoints,
    showLineMeasurements,
    setShowLineMeasurements,
    showPointMeasurements,
    setShowPointMeasurements,
  } = useStoreWithEqualityFn(
    useUnityBuildStore,
    state => ({
      setAngleOptions: state.setAngleOptions,
      isAnnotatingTakeoff: state.isAnnotatingTakeoff,
      setIsAnnotatingTakeoff: state.setIsAnnotatingTakeoff,
      setUserScale: state.setUserScale,
      selectedObject: state.selectedObject,
      setSelectedObject: state.setSelectedObject,
      isLassoMode: state.isLassoMode,
      setIsLassoMode: state.setIsLassoMode,
      setIsLassoDrawing: state.setIsLassoDrawing,
      setLassoPoints: state.setLassoPoints,
      showLineMeasurements: state.showLineMeasurements,
      setShowLineMeasurements: state.setShowLineMeasurements,
      showPointMeasurements: state.showPointMeasurements,
      setShowPointMeasurements: state.setShowPointMeasurements,
    }),
    shallow
  );

  const { setCurrentTakeoffAssembly } = useStoreWithEqualityFn(
    useAssemblyStore,
    state => ({
      setCurrentTakeoffAssembly: state.setCurrentTakeoffAssembly,
      currentTakeoffAssembly: state.currentTakeoffAssembly,
    }),
    shallow
  );

  // is annotating is used for when we are adding notes or shapes that are not takeoff
  const {
    isAnnotating,
    activeAnnotationType,
    setActiveAnnotationType,
    setIsAnnotating,
    showCursorTooltip,
    setShowCursorTooltip,
    annotationVisibilityMode,
    setAnnotationVisibilityMode,
    isDimensionalMode,
    setIsDimensionalMode,
  } = useStoreWithEqualityFn(
    useAnnotationStore,
    state => ({
      isAnnotating: state.isAnnotating,
      activeAnnotationType: state.activeAnnotationType,
      setActiveAnnotationType: state.setActiveAnnotationType,
      setIsAnnotating: state.setIsAnnotating,
      showCursorTooltip: state.showCursorTooltip,
      setShowCursorTooltip: state.setShowCursorTooltip,
      annotationVisibilityMode: state.annotationVisibilityMode,
      setAnnotationVisibilityMode: state.setAnnotationVisibilityMode,
      isDimensionalMode: state.isDimensionalMode,
      setIsDimensionalMode: state.setIsDimensionalMode,
    }),
    shallow
  );

  const {
    keyBoardEventStore,
    setSnapMode,
    snapMode,
    enableCrosshair,
    isMeasuring,
    isViewingFileManager,
    setIsViewingFileManager,
    continousMode,
    setContinousMode,
    setIsMeasuring,
    setEnableCrosshair,
    takeoffPaletteVisibilityMode,
    setTakeoffPaletteVisibilityMode,
  } = useStoreWithEqualityFn(
    useKeyBoardEventStore,
    state => ({
      keyBoardEventStore: state,
      setSnapMode: state.setSnapMode,
      snapMode: state.snapMode,
      enableCrosshair: state.enableCrosshair,
      isMeasuring: state.isMeasuring,
      isViewingTakeoffPallette: state.isViewingTakeoffPallette,
      setIsViewingTakeoffPallette: state.setIsViewingTakeoffPallette,
      isViewingFileManager: state.isViewingFileManager,
      setIsViewingFileManager: state.setIsViewingFileManager,
      continousMode: state.continousMode,
      setContinousMode: state.setContinousMode,
      setIsMeasuring: state.setIsMeasuring,
      setEnableCrosshair: state.setEnableCrosshair,
      takeoffPaletteVisibilityMode: state.takeoffPaletteVisibilityMode,
      setTakeoffPaletteVisibilityMode: state.setTakeoffPaletteVisibilityMode,
    }),
    shallow
  );

  // Get selection state for flip functionality
  const { isFlipped, toggleFlip } = useStoreWithEqualityFn(
    useSelectionStore,
    state => ({
      isFlipped: state.isFlipped,
      toggleFlip: state.toggleFlip,
    }),
    shallow
  );

  const { submitAssembly } = useSubmitTakeoffAssembly();
  const { data: fetchedAssemblies } = useFetchCountedAssemblies();
  const {
    data: availableObjects,
    refetchObjects,
    documentStatus,
    markAllAsObserved,
  } = useFetchObjects();

  const [currentAngleOption, setCurrentAngleOption] = useState<number>(5);
  const [isCreatingScale, setIsCreatingScale] = useState<boolean>(false);
  const [selectedDocument, setSelectedDocument] = useState<ExtendedObject | null>(null);
  const [shouldSubmit, setShouldSubmit] = useState(false);
  const [isInferenceServiceHealthy, setIsInferenceServiceHealthy] = useState<boolean>(false);
  const [showServiceHealthDialog, setShowServiceHealthDialog] = useState<boolean>(false);

  const localScale = useRef<number>(0);

  // Monitor service health when DocumentManagerToggle is open
  useEffect(() => {
    if (isViewingFileManager && !isInferenceServiceHealthy) {
      setShowServiceHealthDialog(true);
    } else if (isViewingFileManager && isInferenceServiceHealthy) {
      setShowServiceHealthDialog(false);
    }
  }, [isViewingFileManager, isInferenceServiceHealthy]);

  // Use centralized keyboard events
  const svgRef = useRef<SVGSVGElement>(null);
  useKeyboardEvents({ svgRef, enabled: true });

  // Get text color based on background or theme
  const getTextColor = () => {
    if (darkMode) {
      return getContrastTextColor(muiTheme.palette.background.paper);
    }
    return muiTheme.palette.text.primary;
  };

  //we must set the local and global scales to the same value when the selected object changes
  useEffect(() => {
    localScale.current = selectedObject?.userscalesList[0]?.scale || 0;
    setUserScale(selectedObject?.userscalesList[0]?.scale || 0);
    // eslint-disable-next-line
  }, [selectedObject]);

  // Initialize angle snap to 5 degrees by default
  useEffect(() => {
    const angleOptions = Array.from({ length: 360 / 5 }, (_, i) => i * 5);
    setAngleOptions(angleOptions);
    setSnapMode(true);
  }, [setAngleOptions, setSnapMode]);

  // Return true if object id has assemblies (takeoff data)
  const isDocumentWithTakeoff = (id: string) => {
    if (!countedAssemblies || Array.isArray(countedAssemblies)) return false;

    const unfilteredAssemblies = countedAssemblies?.unfilteredAssemblies || [];

    // Check if any assembly in any takeoff has a matching objectid
    return unfilteredAssemblies.some(takeoff =>
      takeoff.assembliesList.some((assembly: any) => assembly.objectid === id)
    );
  };

  // CRITICAL: This useEffect is essential for maintaining accurate measurements.
  // When the scale changes (localScale.current), we MUST update all takeoff assemblies on the current document
  // to recalculate their real-world dimensions based on the new scale.
  // This ensures that all length and area measurements reflect the correct real-world values.
  // DO NOT REMOVE OR MODIFY THIS EFFECT without understanding the implications.
  useEffect(() => {
    if (!shouldSubmit) return;

    const submitAssembliesWithUpdatedScale = async () => {
      try {
        if (
          !fetchedAssemblies ||
          Array.isArray(fetchedAssemblies) ||
          !fetchedAssemblies.filteredAssemblies ||
          fetchedAssemblies.filteredAssemblies.length === 0 ||
          localScale.current <= 0
        )
          return;
        for (const takeoff of fetchedAssemblies.filteredAssemblies) {
          for (const assembly of takeoff.assembliesList) {
            if (
              (assembly.assemblymeasurementtype === 'length' ||
                assembly.assemblymeasurementtype === 'area') &&
              assembly.objectid === selectedObject?.id
            ) {
              await submitAssembly({
                shouldUpdateBreakout: false,
                assembly,
                isUpdating: true,
                isCreating: false,
                pageScale: localScale.current,
              });
            }
          }
        }
        setUserScale(localScale.current);
        setShouldSubmit(false);
        refetchObjects();
      } catch (error) {
        console.error('Error updating assemblies with new scale:', error);
      }
    };

    submitAssembliesWithUpdatedScale();
    //eslint-disable-next-line
  }, [shouldSubmit, fetchedAssemblies, selectedObject?.id]);

  // Listen for hyperlink navigation requests
  useEffect(() => {
    const handleNavigateToObject = (e: any) => {
      const objectId = e?.detail?.objectId as string;
      if (!objectId) return;
      const target = (availableObjects || []).find((o: any) => o.id === objectId);
      if (target) {
        setSelectedObject(target);
        setSelectedDocument(target as unknown as ExtendedObject);
      }
    };
    document.addEventListener('app:navigateToObject', handleNavigateToObject as EventListener);

    return () => {
      document.removeEventListener('app:navigateToObject', handleNavigateToObject as EventListener);
    };
  }, [availableObjects, setSelectedObject]);

  // Toggle file manager
  const handleFileManager = () => {
    setIsViewingFileManager(!isViewingFileManager);
  };

  const handleGetObject = (event: SelectChangeEvent<number>) => {
    if (availableObjects) {
      const selectedIndex = Number(event.target.value);
      if (selectedIndex >= 0) {
        const selectedDoc = availableObjects[selectedIndex];
        setSelectedObject(selectedDoc || null);
        setSelectedDocument(selectedDoc as ExtendedObject);

        // Check if paste preview is active
        const isPastePreviewActive = (window as any).__pastePreviewActive;

        // Get current copy mode
        const selectionStore = useSelectionStore.getState();
        const currentCopyMode = selectionStore.copyMode;

        if (isPastePreviewActive) {
          // If copy mode is 'extend', cancel paste preview when changing documents
          // as extending should only work on the original document
          if (currentCopyMode === 'extend') {
            // Clear paste preview
            document.dispatchEvent(new CustomEvent('app:clearpastepreview'));
            // Clear copied items
            selectionStore.clearCopiedItems();
            // Clear current takeoff assembly
            setCurrentTakeoffAssembly(null);
          } else {
            // For 'create' mode (Copy as New), preserve the paste preview
            // Do not clear currentTakeoffAssembly to allow cross-page pasting
          }
        } else {
          // No active paste preview, normal behavior
          setCurrentTakeoffAssembly(null);
        }

        // Mark all documents as observed after selection
        // This will also reset the recentUpload flag
        if (documentStatus?.newDocumentsDetected || documentStatus?.recentUpload) {
          markAllAsObserved();
        }
      }
    }
  };

  // Open the doc in a new tab
  const handleOpenDocument = () => {
    if (selectedDocument?.previewUrl) {
      window.open(selectedDocument.previewUrl, '_blank', 'noopener,noreferrer');
    } else {
      alert('Please select a document to open.');
    }
  };

  // Set scale from a <select>
  const handleSetScale = (event: SelectChangeEvent<number>) => {
    const value = Number(event.target.value);

    // If custom scale selected, always show the CustomScale component
    // NOTE: We always want to show the dialog when explicitly selecting Custom Scale,
    // even if a custom scale value is already set
    if (value === -1) {
      setIsCreatingScale(true);
      return;
    }

    // If selecting a preset scale or the current custom scale
    setShouldSubmit(true);
    localScale.current = value;

    // Create a simulated event for the original handler
    const simulatedEvent = {
      target: {
        value: String(value),
      },
    } as React.ChangeEvent<HTMLSelectElement>;

    ToolBarHelper.handleSetScale(
      simulatedEvent,
      setUserScale,
      setIsCreatingScale,
      selectedObject,
      refetchObjects
    );
  };

  // Toggle crosshair
  const handleEnableCrosshair = () => {
    setEnableCrosshair(!enableCrosshair);
  };

  // Start/stop measuring
  const handleMeasure = () => {
    ResetStates.resetTakeoffStates(ubs, globalAssemblyStore, keyBoardEventStore);
    setIsAnnotatingTakeoff(!isAnnotatingTakeoff);
    setIsMeasuring(!isMeasuring);

    // Only clear measured points when starting measuring mode, not when stopping
    if (!isMeasuring) {
      ubs.setMeasuredPoints([]);
    }
  };

  // Angle snaps
  const handleSetSnaps = (event: SelectChangeEvent<string>) => {
    const value = Number(event.target.value);
    setCurrentAngleOption(value);

    if (value === 0) {
      setSnapMode(false);
      setAngleOptions([]);
      return;
    }

    // const angleOption = parseInt(value, 10);
    const angleOptions = Array.from({ length: 360 / value }, (_, i) => i * value);
    setAngleOptions(angleOptions);
    setSnapMode(true);
  };

  // Our toggle for "continuous mode" or "non-continuous mode"
  const handleToggleContinuousMode = () => {
    setContinousMode(!continousMode); // flip it in the Zustand store
  };

  // Within the ToolBar component, add a function to clear measurements
  const handleClearMeasurements = () => {
    ubs.setMeasuredPoints([]);
  };

  // Helper function to handle annotation type changes
  const handleAnnotationTypeChange = (type: any) => {
    if (activeAnnotationType === type && isAnnotating) {
      // If clicking the same active tool, deactivate it
      setActiveAnnotationType(null);
      setIsAnnotating(false);
    } else {
      // Activate the selected tool
      setActiveAnnotationType(type);
      setIsAnnotating(true);
    }
  };

  // Add this function to get the status message
  const getDocumentStatusMessage = () => {
    if (documentStatus?.isPolling) {
      return 'Searching for documents...';
    }
    if (documentStatus?.isProcessing) {
      if (documentStatus.totalCount > 0) {
        return `Processing documents (${documentStatus.processedCount}/${documentStatus.totalCount})`;
      }
      return 'Processing documents...';
    }
    if (!availableObjects || availableObjects.length === 0) {
      return 'No documents available';
    }
    return 'Select a document';
  };

  // Return background based on status
  const getStatusBackgroundColor = () => {
    if (documentStatus?.newDocumentsDetected) {
      return alpha(muiTheme.palette.success.main, 0.1);
    }
    if (documentStatus?.isPolling) {
      return alpha(muiTheme.palette.info.main, 0.1);
    }
    return 'transparent';
  };

  // Return text color based on status
  const getStatusTextColor = () => {
    if (documentStatus?.newDocumentsDetected) {
      return muiTheme.palette.success.main;
    }
    if (documentStatus?.isPolling) {
      return muiTheme.palette.info.main;
    }
    if (!availableObjects || availableObjects.length === 0) {
      return muiTheme.palette.warning.main;
    }
    return muiTheme.palette.text.secondary;
  };

  // Calculate progress percent
  const getProgressPercent = () => {
    if (!documentStatus || !documentStatus.totalCount) return 0;
    return Math.min(
      100,
      Math.round((documentStatus.processedCount / documentStatus.totalCount) * 100)
    );
  };

  // Toggle cursor tooltip visibility
  const handleToggleCursorTooltip = () => {
    setShowCursorTooltip(!showCursorTooltip);
  };

  // Cycle through annotation visibility modes: all -> mine -> none -> all
  const handleToggleAnnotations = () => {
    const modes: ('all' | 'mine' | 'none')[] = ['all', 'mine', 'none'];
    const currentIndex = modes.indexOf(annotationVisibilityMode);
    const nextIndex = (currentIndex + 1) % modes.length;
    setAnnotationVisibilityMode(modes[nextIndex]);
  };

  // Cycle through takeoff palette visibility modes: always -> hover -> hidden -> always
  const handleToggleTakeoffPalette = () => {
    const modes: ('always' | 'hover' | 'hidden')[] = ['always', 'hover', 'hidden'];
    const currentIndex = modes.indexOf(takeoffPaletteVisibilityMode);
    const nextIndex = (currentIndex + 1) % modes.length;
    setTakeoffPaletteVisibilityMode(modes[nextIndex]);
  };

  // Get tooltip text for annotation visibility mode
  const getAnnotationTooltipText = () => {
    switch (annotationVisibilityMode) {
      case 'all':
        return 'Show all annotations (click to show only my annotations) (V)';
      case 'mine':
        return 'Show only my annotations (click to hide all annotations) (V)';
      case 'none':
        return 'Hide all annotations (click to show all annotations) (V)';
      default:
        return 'Toggle annotation visibility (V)';
    }
  };

  // Get icon for annotation visibility mode
  const getAnnotationIcon = () => {
    switch (annotationVisibilityMode) {
      case 'all':
        return <VisibilityIcon fontSize="small" />;
      case 'mine':
        return <VisibilityIcon fontSize="small" />;
      case 'none':
        return <VisibilityOffIcon fontSize="small" />;
      default:
        return <VisibilityIcon fontSize="small" />;
    }
  };

  // Get tooltip text for takeoff palette visibility mode
  const getTakeoffPaletteTooltipText = () => {
    switch (takeoffPaletteVisibilityMode) {
      case 'always':
        return 'Takeoff palette always visible (click to show on hover only) (O)';
      case 'hover':
        return 'Takeoff palette shows on hover (click to hide completely) (O)';
      case 'hidden':
        return 'Takeoff palette hidden (click to show always) (O)';
      default:
        return 'Toggle takeoff palette visibility (O)';
    }
  };

  // Get icon for takeoff palette visibility mode
  const getTakeoffPaletteIcon = () => {
    switch (takeoffPaletteVisibilityMode) {
      case 'always':
        return <VisibilityIcon fontSize="small" />;
      case 'hover':
        return <VisibilityIcon fontSize="small" />;
      case 'hidden':
        return <VisibilityOffIcon fontSize="small" />;
      default:
        return <VisibilityIcon fontSize="small" />;
    }
  };

  // Reset annotation state on component mount
  useEffect(() => {
    // Reset annotation state to ensure a clean state on component mount
    const annotationStore = useAnnotationStore.getState();
    annotationStore.setActiveAnnotationType(null);
    annotationStore.setIsAnnotating(false);
  }, []);

  // Listen for the custom scale component's close event
  useEffect(() => {
    const handleCloseCustomScale = () => {
      setIsCreatingScale(false);
    };

    // Handle custom scale updates from the CustomScale component
    const handleCustomScaleUpdated = (event: CustomEvent) => {
      if (event.detail && event.detail.scale) {
        localScale.current = event.detail.scale;
        // CRITICAL: When scale changes, we must set shouldSubmit to true to trigger
        // the recalculation of all takeoff assemblies on the current document
        setShouldSubmit(true);
      }
    };

    document.addEventListener('close-custom-scale', handleCloseCustomScale);
    document.addEventListener('custom-scale-updated', handleCustomScaleUpdated as EventListener);

    return () => {
      document.removeEventListener('close-custom-scale', handleCloseCustomScale);
      document.removeEventListener(
        'custom-scale-updated',
        handleCustomScaleUpdated as EventListener
      );
    };
  }, []);

  return (
    <>
      <AppBar
        position="relative"
        color="default"
        elevation={1}
        sx={{
          backgroundColor: muiTheme.palette.background.paper,
          color: getTextColor(),
          zIndex: Z_INDEX_LAYERS.UI_COMPONENTS.TOOLBAR,
          mt: 0.5,
          borderTop: `1px solid ${alpha(getTextColor(), 0.05)}`,
          boxShadow: 'none',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            minHeight: 56,
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          {/* Document Selection Group */}
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
            <Tooltip title="Open document manager" arrow>
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                startIcon={<FolderOpenIcon aria-label="Document folder" />}
                onClick={handleFileManager}
                sx={{
                  marginRight: 1,
                  borderColor: alpha(getTextColor(), 0.5),
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: getTextColor(),
                    backgroundColor: alpha(getTextColor(), 0.1),
                  },
                }}
              >
                Document
              </Button>
            </Tooltip>

            <FormControl variant="outlined" size="small" sx={{ minWidth: 240 }}>
              <Select
                displayEmpty
                value={
                  selectedDocument
                    ? (availableObjects?.findIndex(obj => obj.id === selectedDocument.id) ?? -1)
                    : -1
                }
                onChange={handleGetObject}
                onClose={() =>
                  (documentStatus?.newDocumentsDetected || documentStatus?.recentUpload) &&
                  markAllAsObserved()
                }
                input={
                  <StyledSelect
                    darkMode={darkMode}
                    sx={{
                      width: 300,
                      color: darkMode
                        ? muiTheme.palette.common.white
                        : muiTheme.palette.common.black,
                    }}
                  />
                }
                MenuProps={{
                  sx: { zIndex: 1500 },
                  PopoverClasses: { root: 'popoverClass' },
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                  slotProps: {
                    paper: {
                      elevation: 4,
                      sx: {
                        mt: 0.5,
                        backgroundColor: muiTheme.palette.background.paper,
                        '& .MuiMenuItem-root': {
                          color: muiTheme.palette.text.primary,
                          py: 0.75,
                        },
                        '& .MuiMenuItem-root.Mui-selected': {
                          backgroundColor: alpha(muiTheme.palette.primary.main, 0.15),
                          color: muiTheme.palette.primary.main,
                          fontWeight: 500,
                        },
                        '& .MuiMenuItem-root:hover': {
                          backgroundColor: alpha(muiTheme.palette.action.hover, 0.1),
                        },
                        '& .MuiMenuItem-root.Mui-selected:hover': {
                          backgroundColor: alpha(muiTheme.palette.primary.main, 0.25),
                        },
                      },
                    },
                  },
                }}
                renderValue={selected => {
                  if (selected === -1) {
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          padding:
                            documentStatus?.isPolling || documentStatus?.isProcessing
                              ? '4px 8px'
                              : 0,
                          margin:
                            documentStatus?.isPolling || documentStatus?.isProcessing
                              ? '-4px -8px'
                              : 0,
                          borderRadius: 1,
                          backgroundColor: getStatusBackgroundColor(),
                        }}
                      >
                        {(documentStatus?.isProcessing || documentStatus?.isPolling) && (
                          <CircularProgress size={16} sx={{ color: getStatusTextColor() }} />
                        )}
                        <Typography
                          variant="body2"
                          sx={{
                            color: getStatusTextColor(),
                            fontWeight: 500,
                          }}
                        >
                          {getDocumentStatusMessage()}
                        </Typography>

                        {documentStatus?.isProcessing && documentStatus.totalCount > 0 && (
                          <Chip
                            label={`${getProgressPercent()}%`}
                            size="small"
                            sx={{
                              height: 20,
                              minWidth: 40,
                              fontSize: '0.7rem',
                              ml: 'auto',
                              backgroundColor: alpha(muiTheme.palette.primary.main, 0.1),
                              color: muiTheme.palette.primary.main,
                            }}
                          />
                        )}
                      </Box>
                    );
                  }
                  return (
                    <Typography variant="body2">
                      {availableObjects?.[selected as number]?.objectname}
                    </Typography>
                  );
                }}
              >
                <MenuItem disabled value={-1}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '100%',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {(documentStatus?.isProcessing || documentStatus?.isPolling) && (
                        <CircularProgress size={16} sx={{ color: getStatusTextColor() }} />
                      )}
                      <Typography
                        variant="body2"
                        sx={{ color: getStatusTextColor(), fontWeight: 500 }}
                      >
                        {getDocumentStatusMessage()}
                      </Typography>

                      {documentStatus?.isProcessing && documentStatus.totalCount > 0 && (
                        <Chip
                          label={`${getProgressPercent()}%`}
                          size="small"
                          sx={{
                            height: 20,
                            minWidth: 40,
                            fontSize: '0.7rem',
                            ml: 'auto',
                            backgroundColor: alpha(muiTheme.palette.primary.main, 0.1),
                            color: muiTheme.palette.primary.main,
                          }}
                        />
                      )}
                    </Box>

                    {(documentStatus?.isProcessing || documentStatus?.isPolling) && (
                      <LinearProgress
                        variant={
                          documentStatus.isProcessing && documentStatus.totalCount > 0
                            ? 'determinate'
                            : 'indeterminate'
                        }
                        value={getProgressPercent()}
                        sx={{
                          mt: 1.5,
                          height: 4,
                          borderRadius: 2,
                          backgroundColor: alpha(muiTheme.palette.primary.main, 0.1),
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: documentStatus.isPolling
                              ? muiTheme.palette.info.main
                              : muiTheme.palette.primary.main,
                          },
                        }}
                      />
                    )}
                  </Box>
                </MenuItem>

                {availableObjects?.map((option, index) => (
                  <MenuItem
                    key={index}
                    value={index}
                    sx={{
                      backgroundColor: isDocumentWithTakeoff(option.id)
                        ? alpha(muiTheme.palette.warning.light, 0.1)
                        : 'transparent',
                      py: 1.5,
                      '&.Mui-selected': {
                        backgroundColor: alpha(muiTheme.palette.primary.main, 0.1),
                        color: muiTheme.palette.primary.main,
                      },
                      '&.Mui-selected:hover': {
                        backgroundColor: alpha(muiTheme.palette.primary.main, 0.2),
                      },
                      color: muiTheme.palette.text.primary,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                      }}
                    >
                      {option.objectname}
                      {isDocumentWithTakeoff(option.id) && (
                        <Chip
                          label="Takeoff"
                          size="small"
                          sx={{
                            ml: 'auto',
                            height: 20,
                            fontSize: '0.7rem',
                            backgroundColor: alpha(muiTheme.palette.warning.light, 0.2),
                            color: muiTheme.palette.warning.dark,
                          }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedDocument && (
              <Tooltip title="Open document in new tab" arrow>
                <IconButton
                  onClick={handleOpenDocument}
                  size="small"
                  sx={{
                    ml: 1,
                    color: getTextColor(),
                    '&:hover': {
                      backgroundColor: alpha(getTextColor(), 0.1),
                    },
                  }}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          <Divider
            orientation="vertical"
            flexItem
            sx={{ mx: 1, backgroundColor: alpha(getTextColor(), 0.2) }}
          />

          {/* Scale Selection */}
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
            <Tooltip title="Set document scale" arrow>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ScaleIcon
                  fontSize="small"
                  sx={{
                    mr: 1,
                    color: getTextColor(),
                  }}
                />
                <FormControl variant="outlined" size="small">
                  <Select
                    disabled={lockBid()}
                    value={
                      // Find if current scale exists in options
                      SCALE_OPTIONS.some(option => option.value === localScale.current)
                        ? localScale.current || 0
                        : localScale.current !== 0
                          ? localScale.current
                          : -1 // Allow reopening custom dialog
                    }
                    onChange={handleSetScale}
                    input={
                      <StyledSelect
                        darkMode={darkMode}
                        sx={{
                          width: 140,
                          color: darkMode
                            ? muiTheme.palette.common.white
                            : muiTheme.palette.common.black,
                        }}
                      />
                    }
                    renderValue={selected => {
                      // Check if selected value is in SCALE_OPTIONS
                      const scaleOption = SCALE_OPTIONS.find(
                        option => option.value === Number(selected)
                      );

                      // If custom value is not in options, show as custom
                      if (!scaleOption && selected !== 0 && selected !== -1) {
                        const customValue = localScale.current;
                        return (
                          <Typography variant="body2">
                            {`1" = ${customValue.toFixed(3)}ft`}
                          </Typography>
                        );
                      }

                      return (
                        <Typography
                          variant="body2"
                          sx={{
                            color:
                              Number(selected) === 0 ? muiTheme.palette.warning.main : 'inherit',
                            fontWeight: Number(selected) === 0 ? 500 : 400,
                          }}
                        >
                          {scaleOption?.label || 'Select scale'}
                        </Typography>
                      );
                    }}
                    MenuProps={{
                      sx: { zIndex: 1500 },
                      PopoverClasses: { root: 'popoverClass' },
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      },
                      slotProps: {
                        paper: {
                          elevation: 4,
                          sx: {
                            mt: 0.5,
                            backgroundColor: muiTheme.palette.background.paper,
                            '& .MuiMenuItem-root': {
                              color: muiTheme.palette.text.primary,
                              py: 0.75,
                            },
                            '& .MuiMenuItem-root.Mui-selected': {
                              backgroundColor: alpha(muiTheme.palette.primary.main, 0.15),
                              color: muiTheme.palette.primary.main,
                              fontWeight: 500,
                            },
                            '& .MuiMenuItem-root:hover': {
                              backgroundColor: alpha(muiTheme.palette.action.hover, 0.1),
                            },
                          },
                        },
                      },
                    }}
                  >
                    {SCALE_OPTIONS.map((option, index) => {
                      // Skip the -1 (Custom Scale) option for now, we'll add it separately
                      if (option.value === -1) return null;

                      return (
                        <MenuItem
                          key={index}
                          value={option.value}
                          sx={{
                            fontWeight: option.value === 0 || option.value === -1 ? 500 : 400,
                            ...(option.value === 0 && {
                              color: muiTheme.palette.warning.main,
                              borderTop: `1px solid ${alpha(muiTheme.palette.divider, 0.5)}`,
                              marginTop: 1,
                              paddingTop: 1,
                            }),
                          }}
                        >
                          {option.label}
                        </MenuItem>
                      );
                    })}

                    {/* Always show the Custom Scale option */}
                    <MenuItem
                      key="custom-scale-option"
                      value={-1}
                      sx={{
                        color: muiTheme.palette.primary.main,
                        fontWeight: 500,
                        borderTop: `1px solid ${alpha(muiTheme.palette.divider, 0.5)}`,
                        marginTop: 1,
                        paddingTop: 1,
                      }}
                    >
                      Custom Scale
                    </MenuItem>

                    {/* Add special menu item for current custom scale if one exists */}
                    {!SCALE_OPTIONS.some(option => option.value === localScale.current) &&
                      localScale.current !== 0 && (
                        <MenuItem
                          key="custom-current"
                          value={localScale.current}
                          sx={{
                            color: muiTheme.palette.info.main,
                            fontWeight: 500,
                            borderTop: `1px solid ${alpha(muiTheme.palette.divider, 0.5)}`,
                            marginTop: 1,
                            paddingTop: 1,
                          }}
                        >
                          {`Current Custom: 1" = ${localScale.current.toFixed(3)}ft`}
                        </MenuItem>
                      )}
                  </Select>
                </FormControl>
              </Box>
            </Tooltip>
          </Box>

          <Divider
            orientation="vertical"
            flexItem
            sx={{ mx: 1, backgroundColor: alpha(getTextColor(), 0.2) }}
          />

          {/* Measurement Tools */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
              borderLeft: `1px solid ${alpha(getTextColor(), 0.15)}`,
              pl: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip
                title={enableCrosshair ? 'Disable crosshair (T)' : 'Enable crosshair (T)'}
                arrow
              >
                <IconButton
                  onClick={handleEnableCrosshair}
                  size="small"
                  color={enableCrosshair ? 'primary' : 'default'}
                  sx={{
                    color: enableCrosshair ? muiTheme.palette.primary.main : getTextColor(),
                    backgroundColor: enableCrosshair
                      ? alpha(muiTheme.palette.primary.main, 0.1)
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: enableCrosshair
                        ? alpha(muiTheme.palette.primary.main, 0.2)
                        : alpha(getTextColor(), 0.1),
                    },
                  }}
                >
                  <AddLocationIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title={isMeasuring ? 'Stop measuring (M)' : 'Start measuring (M)'} arrow>
                <IconButton
                  onClick={handleMeasure}
                  disabled={haveCurrentTakeoffAssembly(currentTakeoffAssembly!)}
                  size="small"
                  color={isMeasuring ? 'primary' : 'default'}
                  sx={{
                    color: isMeasuring ? muiTheme.palette.primary.main : getTextColor(),
                    backgroundColor: isMeasuring
                      ? alpha(muiTheme.palette.primary.main, 0.1)
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: isMeasuring
                        ? alpha(muiTheme.palette.primary.main, 0.2)
                        : alpha(getTextColor(), 0.1),
                    },
                  }}
                >
                  <StraightenIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Clear all measurements (X)" arrow>
                <IconButton
                  onClick={handleClearMeasurements}
                  size="small"
                  sx={{
                    color: getTextColor(),
                    '&:hover': {
                      backgroundColor: alpha(getTextColor(), 0.1),
                    },
                  }}
                >
                  <DeleteSweepIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              {/* Line Measurements Toggle */}
              <Tooltip
                title={
                  showLineMeasurements ? 'Hide line measurements (Q)' : 'Show line measurements (Q)'
                }
                arrow
              >
                <IconButton
                  onClick={() => setShowLineMeasurements(!showLineMeasurements)}
                  size="small"
                  color={showLineMeasurements ? 'primary' : 'default'}
                  sx={{
                    color: showLineMeasurements ? muiTheme.palette.primary.main : getTextColor(),
                    backgroundColor: showLineMeasurements
                      ? alpha(muiTheme.palette.primary.main, 0.1)
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: showLineMeasurements
                        ? alpha(muiTheme.palette.primary.main, 0.2)
                        : alpha(getTextColor(), 0.1),
                    },
                  }}
                >
                  <SquareFootIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              {/* Point Measurements Toggle */}
              <Tooltip
                title={
                  showPointMeasurements
                    ? 'Hide point measurements (W)'
                    : 'Show point measurements (W)'
                }
                arrow
              >
                <IconButton
                  onClick={() => setShowPointMeasurements(!showPointMeasurements)}
                  size="small"
                  color={showPointMeasurements ? 'primary' : 'default'}
                  sx={{
                    color: showPointMeasurements ? muiTheme.palette.primary.main : getTextColor(),
                    backgroundColor: showPointMeasurements
                      ? alpha(muiTheme.palette.primary.main, 0.1)
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: showPointMeasurements
                        ? alpha(muiTheme.palette.primary.main, 0.2)
                        : alpha(getTextColor(), 0.1),
                    },
                  }}
                >
                  <HeightIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.65rem',
                color: alpha(getTextColor(), 0.6),
                textAlign: 'center',
                lineHeight: 1,
              }}
            >
              Measurement
            </Typography>
          </Box>

          {/* Selection & Editing Tools */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
              borderLeft: `1px solid ${alpha(getTextColor(), 0.15)}`,
              pl: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Multi-Select Toggle removed */}
              {/* Lasso Selection */}
              <Tooltip
                title={isLassoMode ? 'Disable lasso select (S)' : 'Enable lasso select (S)'}
                arrow
              >
                <IconButton
                  onClick={() => {
                    const next = !isLassoMode;
                    setIsLassoMode(next);
                    setIsLassoDrawing(false);
                    setLassoPoints([]);
                  }}
                  disabled={haveCurrentTakeoffAssembly(currentTakeoffAssembly!)}
                  size="small"
                  color={isLassoMode ? 'primary' : 'default'}
                  sx={{
                    color: isLassoMode ? muiTheme.palette.primary.main : getTextColor(),
                    backgroundColor: isLassoMode
                      ? alpha(muiTheme.palette.primary.main, 0.1)
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: isLassoMode
                        ? alpha(muiTheme.palette.primary.main, 0.2)
                        : alpha(getTextColor(), 0.1),
                    },
                  }}
                >
                  <GestureIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              {/* Mirror Image Toggle */}
              <Tooltip
                title={
                  isFlipped
                    ? 'Mirror image ON - copied points will be flipped horizontally (F)'
                    : 'Mirror image OFF - click to enable horizontal flipping for copy/paste (F)'
                }
                arrow
              >
                <IconButton
                  onClick={toggleFlip}
                  size="small"
                  color={isFlipped ? 'primary' : 'default'}
                  sx={{
                    color: isFlipped ? muiTheme.palette.primary.main : getTextColor(),
                    backgroundColor: isFlipped
                      ? alpha(muiTheme.palette.primary.main, 0.1)
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: isFlipped
                        ? alpha(muiTheme.palette.primary.main, 0.2)
                        : alpha(getTextColor(), 0.1),
                    },
                  }}
                >
                  <FlipIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Annotation Tools */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
              borderLeft: `1px solid ${alpha(getTextColor(), 0.15)}`,
              pl: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title="Add a text note (N)" arrow>
                <IconButton
                  onClick={() => handleAnnotationTypeChange('text')}
                  disabled={haveCurrentTakeoffAssembly(currentTakeoffAssembly!)}
                  size="small"
                  color={activeAnnotationType === 'text' ? 'primary' : 'default'}
                  sx={{
                    color:
                      activeAnnotationType === 'text'
                        ? muiTheme.palette.primary.main
                        : getTextColor(),
                    backgroundColor:
                      activeAnnotationType === 'text'
                        ? alpha(muiTheme.palette.primary.main, 0.1)
                        : 'transparent',
                    '&:hover': {
                      backgroundColor:
                        activeAnnotationType === 'text'
                          ? alpha(muiTheme.palette.primary.main, 0.2)
                          : alpha(getTextColor(), 0.1),
                    },
                  }}
                >
                  <EditNoteIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Draw rectangle (R)" arrow>
                <IconButton
                  onClick={() => handleAnnotationTypeChange('rectangle')}
                  disabled={haveCurrentTakeoffAssembly(currentTakeoffAssembly!)}
                  size="small"
                  color={activeAnnotationType === 'rectangle' ? 'primary' : 'default'}
                  sx={{
                    color:
                      activeAnnotationType === 'rectangle'
                        ? muiTheme.palette.primary.main
                        : getTextColor(),
                    backgroundColor:
                      activeAnnotationType === 'rectangle'
                        ? alpha(muiTheme.palette.primary.main, 0.1)
                        : 'transparent',
                    '&:hover': {
                      backgroundColor:
                        activeAnnotationType === 'rectangle'
                          ? alpha(muiTheme.palette.primary.main, 0.2)
                          : alpha(getTextColor(), 0.1),
                    },
                  }}
                >
                  <RectangleIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Draw ellipse (or circle) (E)" arrow>
                <IconButton
                  onClick={() => handleAnnotationTypeChange('ellipse')}
                  disabled={haveCurrentTakeoffAssembly(currentTakeoffAssembly!)}
                  size="small"
                  color={activeAnnotationType === 'ellipse' ? 'primary' : 'default'}
                  sx={{
                    color:
                      activeAnnotationType === 'ellipse'
                        ? muiTheme.palette.primary.main
                        : getTextColor(),
                    backgroundColor:
                      activeAnnotationType === 'ellipse'
                        ? alpha(muiTheme.palette.primary.main, 0.1)
                        : 'transparent',
                    '&:hover': {
                      backgroundColor:
                        activeAnnotationType === 'ellipse'
                          ? alpha(muiTheme.palette.primary.main, 0.2)
                          : alpha(getTextColor(), 0.1),
                    },
                  }}
                >
                  <CircleIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Draw line (L)" arrow>
                <IconButton
                  onClick={() => handleAnnotationTypeChange('line')}
                  disabled={haveCurrentTakeoffAssembly(currentTakeoffAssembly!)}
                  size="small"
                  color={activeAnnotationType === 'line' ? 'primary' : 'default'}
                  sx={{
                    color:
                      activeAnnotationType === 'line'
                        ? muiTheme.palette.primary.main
                        : getTextColor(),
                    backgroundColor:
                      activeAnnotationType === 'line'
                        ? alpha(muiTheme.palette.primary.main, 0.1)
                        : 'transparent',
                    '&:hover': {
                      backgroundColor:
                        activeAnnotationType === 'line'
                          ? alpha(muiTheme.palette.primary.main, 0.2)
                          : alpha(getTextColor(), 0.1),
                    },
                  }}
                >
                  <TimelineIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Draw polyline (multi-segment line with angle snap) (P)" arrow>
                <IconButton
                  onClick={() => handleAnnotationTypeChange('polyline')}
                  disabled={haveCurrentTakeoffAssembly(currentTakeoffAssembly!)}
                  size="small"
                  color={activeAnnotationType === 'polyline' ? 'primary' : 'default'}
                  sx={{
                    color:
                      activeAnnotationType === 'polyline'
                        ? muiTheme.palette.primary.main
                        : getTextColor(),
                    backgroundColor:
                      activeAnnotationType === 'polyline'
                        ? alpha(muiTheme.palette.primary.main, 0.1)
                        : 'transparent',
                    '&:hover': {
                      backgroundColor:
                        activeAnnotationType === 'polyline'
                          ? alpha(muiTheme.palette.primary.main, 0.2)
                          : alpha(getTextColor(), 0.1),
                    },
                  }}
                >
                  <PolylineIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Draw polygon (closed shape with angle snap) (G)" arrow>
                <IconButton
                  onClick={() => handleAnnotationTypeChange('polygon')}
                  disabled={haveCurrentTakeoffAssembly(currentTakeoffAssembly!)}
                  size="small"
                  color={activeAnnotationType === 'polygon' ? 'primary' : 'default'}
                  sx={{
                    color:
                      activeAnnotationType === 'polygon'
                        ? muiTheme.palette.primary.main
                        : getTextColor(),
                    backgroundColor:
                      activeAnnotationType === 'polygon'
                        ? alpha(muiTheme.palette.primary.main, 0.1)
                        : 'transparent',
                    '&:hover': {
                      backgroundColor:
                        activeAnnotationType === 'polygon'
                          ? alpha(muiTheme.palette.primary.main, 0.2)
                          : alpha(getTextColor(), 0.1),
                    },
                  }}
                >
                  <CategoryIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Draw arrow (A)" arrow>
                <IconButton
                  onClick={() => handleAnnotationTypeChange('arrow')}
                  disabled={haveCurrentTakeoffAssembly(currentTakeoffAssembly!)}
                  size="small"
                  color={activeAnnotationType === 'arrow' ? 'primary' : 'default'}
                  sx={{
                    color:
                      activeAnnotationType === 'arrow'
                        ? muiTheme.palette.primary.main
                        : getTextColor(),
                    backgroundColor:
                      activeAnnotationType === 'arrow'
                        ? alpha(muiTheme.palette.primary.main, 0.1)
                        : 'transparent',
                    '&:hover': {
                      backgroundColor:
                        activeAnnotationType === 'arrow'
                          ? alpha(muiTheme.palette.primary.main, 0.2)
                          : alpha(getTextColor(), 0.1),
                    },
                  }}
                >
                  <ArrowForwardIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Highlighter (H)" arrow>
                <IconButton
                  onClick={() => handleAnnotationTypeChange('highlighter')}
                  disabled={haveCurrentTakeoffAssembly(currentTakeoffAssembly!)}
                  size="small"
                  color={activeAnnotationType === 'highlighter' ? 'primary' : 'default'}
                  sx={{
                    color:
                      activeAnnotationType === 'highlighter'
                        ? muiTheme.palette.primary.main
                        : getTextColor(),
                    backgroundColor:
                      activeAnnotationType === 'highlighter'
                        ? alpha(muiTheme.palette.primary.main, 0.1)
                        : 'transparent',
                    '&:hover': {
                      backgroundColor:
                        activeAnnotationType === 'highlighter'
                          ? alpha(muiTheme.palette.primary.main, 0.2)
                          : alpha(getTextColor(), 0.1),
                    },
                  }}
                >
                  <FormatColorFillIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Create hyperlink (K)" arrow>
                <IconButton
                  onClick={() => handleAnnotationTypeChange('hyperlink')}
                  disabled={haveCurrentTakeoffAssembly(currentTakeoffAssembly!)}
                  size="small"
                  color={activeAnnotationType === 'hyperlink' ? 'primary' : 'default'}
                  sx={{
                    color:
                      activeAnnotationType === 'hyperlink'
                        ? muiTheme.palette.primary.main
                        : getTextColor(),
                    backgroundColor:
                      activeAnnotationType === 'hyperlink'
                        ? alpha(muiTheme.palette.primary.main, 0.1)
                        : 'transparent',
                    '&:hover': {
                      backgroundColor:
                        activeAnnotationType === 'hyperlink'
                          ? alpha(muiTheme.palette.primary.main, 0.2)
                          : alpha(getTextColor(), 0.1),
                    },
                  }}
                >
                  <LinkIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.65rem',
                color: alpha(getTextColor(), 0.6),
                textAlign: 'center',
                lineHeight: 1,
              }}
            >
              {isDimensionalMode ? 'Dimensional Annotations' : 'Annotations'}
            </Typography>
          </Box>

          {/* Dimensional Mode Controls */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
              borderLeft: `1px solid ${alpha(getTextColor(), 0.15)}`,
              pl: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip
                title={
                  isDimensionalMode
                    ? 'Dimensional mode ON - annotations will include measurements (D)'
                    : 'Dimensional mode OFF - annotations will not include measurements (D)'
                }
                arrow
              >
                <IconButton
                  onClick={() => setIsDimensionalMode(!isDimensionalMode)}
                  size="small"
                  color={isDimensionalMode ? 'primary' : 'default'}
                  sx={{
                    color: isDimensionalMode ? muiTheme.palette.primary.main : getTextColor(),
                    backgroundColor: isDimensionalMode
                      ? alpha(muiTheme.palette.primary.main, 0.1)
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: isDimensionalMode
                        ? alpha(muiTheme.palette.primary.main, 0.2)
                        : alpha(getTextColor(), 0.1),
                    },
                  }}
                >
                  <SquareFootIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.65rem',
                color: alpha(getTextColor(), 0.6),
                textAlign: 'center',
                lineHeight: 1,
              }}
            >
              Dimensional
            </Typography>
          </Box>

          <Divider
            orientation="vertical"
            flexItem
            sx={{ mx: 1, backgroundColor: alpha(getTextColor(), 0.2) }}
          />

          {/* Snap & Link Tools */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
              borderLeft: `1px solid ${alpha(getTextColor(), 0.15)}`,
              pl: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AngleSnapIcon isActive={snapMode} color={getTextColor()} />
              <FormControl variant="outlined" size="small">
                <Select
                  value={String(currentAngleOption)}
                  onChange={handleSetSnaps}
                  input={
                    <StyledSelect
                      darkMode={darkMode}
                      sx={{
                        width: 100,
                        color: darkMode
                          ? muiTheme.palette.common.white
                          : muiTheme.palette.common.black,
                        backgroundColor: snapMode
                          ? alpha(muiTheme.palette.primary.main, 0.1)
                          : darkMode
                            ? alpha(muiTheme.palette.background.paper, 0.15)
                            : muiTheme.palette.background.paper,
                      }}
                    />
                  }
                  MenuProps={{
                    sx: { zIndex: 1500 },
                    PopoverClasses: { root: 'popoverClass' },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                    slotProps: {
                      paper: {
                        elevation: 4,
                        sx: {
                          mt: 0.5,
                          backgroundColor: muiTheme.palette.background.paper,
                          '& .MuiMenuItem-root': {
                            color: muiTheme.palette.text.primary,
                            py: 0.75,
                          },
                          '& .MuiMenuItem-root.Mui-selected': {
                            backgroundColor: alpha(muiTheme.palette.primary.main, 0.15),
                            color: muiTheme.palette.primary.main,
                            fontWeight: 500,
                          },
                          '& .MuiMenuItem-root:hover': {
                            backgroundColor: alpha(muiTheme.palette.action.hover, 0.1),
                          },
                          '& .MuiMenuItem-root.Mui-selected:hover': {
                            backgroundColor: alpha(muiTheme.palette.primary.main, 0.25),
                          },
                        },
                      },
                    },
                  }}
                  renderValue={selected => (
                    <Typography
                      variant="body2"
                      sx={{
                        color:
                          selected === ''
                            ? muiTheme.palette.info.main
                            : snapMode
                              ? muiTheme.palette.primary.main
                              : 'inherit',
                        fontWeight: selected === '' ? 500 : 400,
                      }}
                    >
                      {selected === '' ? 'Snap: Off' : `${selected}° snap`}
                    </Typography>
                  )}
                  displayEmpty
                >
                  <MenuItem
                    value=""
                    sx={{
                      fontWeight: 500,
                      color: muiTheme.palette.info.main,
                    }}
                  >
                    <em>Off</em>
                  </MenuItem>
                  {angleOptionMultipliers.map(angle => (
                    <MenuItem key={angle} value={String(angle)}>
                      {angle}° snap
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.65rem',
                color: alpha(getTextColor(), 0.6),
                textAlign: 'center',
                lineHeight: 1,
              }}
            >
              Snap & Link
            </Typography>
          </Box>

          <Divider
            orientation="vertical"
            flexItem
            sx={{ mx: 1, backgroundColor: alpha(getTextColor(), 0.2) }}
          />

          {/* Mode Controls */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
              borderLeft: `1px solid ${alpha(getTextColor(), 0.15)}`,
              pl: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip
                title={
                  continousMode
                    ? 'Switch to non-continuous mode (Ctrl)'
                    : 'Switch to continuous mode (Ctrl)'
                }
                arrow
              >
                <IconButton
                  onClick={handleToggleContinuousMode}
                  size="small"
                  color={continousMode ? 'primary' : 'default'}
                  sx={{
                    color: continousMode ? muiTheme.palette.primary.main : getTextColor(),
                    backgroundColor: continousMode
                      ? alpha(muiTheme.palette.primary.main, 0.1)
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: continousMode
                        ? alpha(muiTheme.palette.primary.main, 0.2)
                        : alpha(getTextColor(), 0.1),
                    },
                  }}
                >
                  {continousMode ? <LinkIcon fontSize="small" /> : <LinkOffIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Box>
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.65rem',
                color: alpha(getTextColor(), 0.6),
                textAlign: 'center',
                lineHeight: 1,
              }}
            >
              Mode
            </Typography>
          </Box>

          {/* Interface Controls - Right Side */}
          <Box
            sx={{
              ml: 'auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
              borderLeft: `1px solid ${alpha(getTextColor(), 0.15)}`,
              pl: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title={getAnnotationTooltipText()} arrow>
                <IconButton
                  onClick={handleToggleAnnotations}
                  size="small"
                  color={annotationVisibilityMode !== 'none' ? 'primary' : 'default'}
                  sx={{
                    color:
                      annotationVisibilityMode !== 'none'
                        ? muiTheme.palette.primary.main
                        : getTextColor(),
                    backgroundColor:
                      annotationVisibilityMode !== 'none'
                        ? alpha(muiTheme.palette.primary.main, 0.1)
                        : 'transparent',
                    border:
                      annotationVisibilityMode === 'all'
                        ? `1px solid ${muiTheme.palette.primary.main}`
                        : '1px solid transparent',
                    '&:hover': {
                      backgroundColor:
                        annotationVisibilityMode !== 'none'
                          ? alpha(muiTheme.palette.primary.main, 0.2)
                          : alpha(getTextColor(), 0.1),
                    },
                  }}
                >
                  {getAnnotationIcon()}
                </IconButton>
              </Tooltip>

              <Tooltip
                title={showCursorTooltip ? 'Hide cursor tooltips (I)' : 'Show cursor tooltips (I)'}
                arrow
              >
                <IconButton
                  onClick={handleToggleCursorTooltip}
                  size="small"
                  color={showCursorTooltip ? 'primary' : 'default'}
                  sx={{
                    color: showCursorTooltip ? muiTheme.palette.primary.main : getTextColor(),
                    backgroundColor: showCursorTooltip
                      ? alpha(muiTheme.palette.primary.main, 0.1)
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: showCursorTooltip
                        ? alpha(muiTheme.palette.primary.main, 0.2)
                        : alpha(getTextColor(), 0.1),
                    },
                  }}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title={getTakeoffPaletteTooltipText()} arrow>
                <IconButton
                  onClick={handleToggleTakeoffPalette}
                  size="small"
                  color={takeoffPaletteVisibilityMode !== 'hidden' ? 'primary' : 'default'}
                  sx={{
                    color:
                      takeoffPaletteVisibilityMode !== 'hidden'
                        ? muiTheme.palette.primary.main
                        : getTextColor(),
                    backgroundColor:
                      takeoffPaletteVisibilityMode !== 'hidden'
                        ? alpha(muiTheme.palette.primary.main, 0.1)
                        : 'transparent',
                    border:
                      takeoffPaletteVisibilityMode === 'always'
                        ? `1px solid ${muiTheme.palette.primary.main}`
                        : '1px solid transparent',
                    '&:hover': {
                      backgroundColor:
                        takeoffPaletteVisibilityMode !== 'hidden'
                          ? alpha(muiTheme.palette.primary.main, 0.2)
                          : alpha(getTextColor(), 0.1),
                    },
                  }}
                >
                  {getTakeoffPaletteIcon()}
                </IconButton>
              </Tooltip>
            </Box>
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.65rem',
                color: alpha(getTextColor(), 0.6),
                textAlign: 'center',
                lineHeight: 1,
              }}
            >
              Interface
            </Typography>
          </Box>
        </Box>
        {isCreatingScale && <CustomScale />}
      </AppBar>

      {isViewingFileManager && (
        <DocumentManagerToggle onClose={() => setIsViewingFileManager(false)} />
      )}

      <ServiceHealthCheck
        open={showServiceHealthDialog}
        serviceName="Document Service"
        customMessage="We are starting up the service, please wait..."
        onServiceHealthy={() => setIsInferenceServiceHealthy(true)}
        onServiceUnhealthy={() => setIsInferenceServiceHealthy(false)}
        onClose={() => setShowServiceHealthDialog(false)}
        onCloseWithDocumentManager={() => {
          setShowServiceHealthDialog(false);
          setIsViewingFileManager(false);
        }}
      />
    </>
  );
};
